import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DatePicker } from '@/components/ui/date-picker'
import {
  CalendarPlus,
  Check,
  ChevronsUpDown,
  Paperclip,
  Users,
  X,
} from 'lucide-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'
import {
  LEAVE_TYPES,
  HALF_DAY_OPTIONS,
  UNPAID_LEAVE_TYPE,
  computeLeaveDaysPreview,
  type HalfDayOption,
} from '@/constant/leave'
import { useGetLeaveBalancesQuery } from '@/redux/service/leaveBalance'
import {
  useCreateLeaveRequestMutation,
  useUploadLeaveAttachmentMutation,
} from '@/redux/service/leaveRequest'
import { useGetEmployeesQuery } from '@/redux/service/employee'
import { useCheckPermission } from '@/hooks/useCheckPermission'

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024

interface RequestLeaveDialogProps {
  isOpen: boolean
  isClose: () => void
  employeeId?: number
  employeeName?: string
}

interface RequestLeaveFormValues {
  leave_type: string
  from_date: string
  from_half: HalfDayOption
  to_date: string
  to_half: HalfDayOption
  reason: string
  notify: number[]
  attachment: File | null
  is_emergency: boolean
}

const RequestLeaveDialog: React.FC<RequestLeaveDialogProps> = ({
  isOpen,
  isClose,
  employeeId,
  employeeName,
}) => {
  const [createLeaveRequest] = useCreateLeaveRequestMutation()
  const [uploadAttachment] = useUploadLeaveAttachmentMutation()
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  // Self-scoped by the backend (see controller/leaveBalance.controller.js) —
  // a non-HR/non-team-leader caller always gets back only their own
  // balances, which is exactly what "days available" needs here.
  const { data: balancesData } = useGetLeaveBalancesQuery(
    {
      status: 'active',
      limit: 50,
      ...(employeeId ? { employee_id: employeeId } : {}),
    },
    { skip: !isOpen }
  )

  // Employee picker for the "Notify" field — same list used elsewhere for
  // team/lead pickers (see TeamFormDialog).
  const { data: employeesData } = useGetEmployeesQuery(
    { page: 1, limit: 500 },
    { skip: !isOpen }
  )

  // The reporting manager is already notified automatically as the approver
  // (see alertbot/leaveNotify.js#notifyLeaveRequestCreated), so exclude them
  // from the pickable "Notify" list rather than requiring the requester to
  // recognize and skip them manually.
  const { user } = useCheckPermission()
  const selfEmail = user?.email?.trim().toLowerCase()
  const selfEmployee = useMemo(() => {
    const employees = employeesData?.data ?? []
    return employees.find(
      e =>
        e.user_email?.trim().toLowerCase() === selfEmail ||
        e.official_email?.trim().toLowerCase() === selfEmail ||
        e.personal_email?.trim().toLowerCase() === selfEmail
    )
  }, [employeesData, selfEmail])
  const reportingManagerName = selfEmployee?.reporting_manager_name
    ?.trim()
    .toLowerCase()

  const notifyOptions = useMemo(
    () =>
      (employeesData?.data ?? []).filter(
        e =>
          e.user_name?.trim() &&
          e.user_name.trim().toLowerCase() !== reportingManagerName
      ),
    [employeesData, reportingManagerName]
  )
  const [notifyPickerOpen, setNotifyPickerOpen] = useState(false)

  const formik = useFormik<RequestLeaveFormValues>({
    initialValues: {
      leave_type: LEAVE_TYPES[0] ?? '',
      from_date: '',
      from_half: 'full',
      to_date: '',
      to_half: 'full',
      reason: '',
      notify: [],
      attachment: null,
      is_emergency: false,
    },
    validationSchema: Yup.object({
      leave_type: Yup.string().required('Leave type is required'),
      from_date: Yup.string().required('From date is required'),
      to_date: Yup.string()
        .required('To date is required')
        .test(
          'after-from',
          'To date must be on or after the from date',
          function (value) {
            return (
              !value || !this.parent.from_date || value >= this.parent.from_date
            )
          }
        ),
      reason: Yup.string().max(2000).required('Note is required'),
      notify: Yup.array()
        .of(Yup.number().required())
        .min(1, 'Select at least one person to notify')
        .required('Notify is required'),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const res = await createLeaveRequest({
          ...(employeeId
            ? { employee_id: employeeId, is_emergency: values.is_emergency }
            : {}),
          leave_type: values.leave_type,
          from_date: values.from_date,
          to_date: values.to_date,
          from_half: values.from_half,
          to_half: values.to_half,
          reason: values.reason,
          notify: values.notify,
        }).unwrap()

        if (values.attachment) {
          try {
            await uploadAttachment({
              taskId: res.data.task_id,
              file: values.attachment,
            }).unwrap()
          } catch {
            toast.error(
              'Leave request submitted, but the attachment failed to upload'
            )
          }
        }

        toast.success(res.message || 'Leave request submitted')
        resetForm()
        isClose()
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Failed to submit leave request'
        toast.error(message)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const isUnpaidLeave = formik.values.leave_type === UNPAID_LEAVE_TYPE

  const availableForType = useMemo(() => {
    const rows = balancesData?.data ?? []
    return rows
      .filter(b => b.leave_type === formik.values.leave_type)
      .reduce((sum, b) => sum + b.balance, 0)
  }, [balancesData, formik.values.leave_type])

  const requestedDays = computeLeaveDaysPreview(
    formik.values.from_date,
    formik.values.to_date,
    formik.values.from_half,
    formik.values.to_half
  )

  const isSameDay =
    !!formik.values.from_date &&
    formik.values.from_date === formik.values.to_date

  // Single-day leave only needs one half-day choice — keep to_half mirrored
  // to from_half so computeLeaveDaysPreview (which looks at both) stays
  // correct even though the "To" dropdown is hidden for this case.
  useEffect(() => {
    if (isSameDay && formik.values.to_half !== formik.values.from_half) {
      formik.setFieldValue('to_half', formik.values.from_half)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameDay, formik.values.from_half])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          formik.resetForm()
          isClose()
        }
      }}
    >
      <DialogContent className='max-w-xl p-0 bg-background border-border rounded-2xl overflow-hidden'>
        <DialogHeader className='px-8 py-6 border-b border-border/50 bg-muted/10'>
          <DialogTitle className='text-xl font-bold flex items-center gap-3'>
            <div className='p-2.5 bg-primary/10 rounded-xl text-primary'>
              <CalendarPlus className='w-5 h-5' />
            </div>
            {employeeName ? `Request Leave — ${employeeName}` : 'Request Leave'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className='p-8 space-y-5'>
          {/* Leave Type */}
          <div className='space-y-2'>
            <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
              Select type of leave you want to apply
            </Label>
            <Select
              value={formik.values.leave_type}
              onValueChange={v => formik.setFieldValue('leave_type', v)}
            >
              <SelectTrigger className='h-11 rounded-xl'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAVE_TYPES.map(t => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              {isUnpaidLeave
                ? 'Unlimited — no balance required'
                : `${availableForType} day(s) available`}
            </p>
          </div>

          {/* From / To dates with half-day selectors — mirrors the Keka
              reference (separate From/To boxes, each with a half-day picker). */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                From
              </Label>
              <DatePicker
                value={formik.values.from_date}
                onChange={v => formik.setFieldValue('from_date', v)}
                placeholder='Pick date'
              />
              <Select
                value={formik.values.from_half}
                onValueChange={v => {
                  formik.setFieldValue('from_half', v)
                  if (isSameDay) formik.setFieldValue('to_half', v)
                }}
              >
                <SelectTrigger className='h-9 rounded-xl text-xs'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HALF_DAY_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formik.touched.from_date && formik.errors.from_date && (
                <p className='text-[11px] font-semibold text-red-500'>
                  {formik.errors.from_date}
                </p>
              )}
            </div>

            <div className='space-y-2'>
              <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
                To
              </Label>
              <DatePicker
                value={formik.values.to_date}
                onChange={v => formik.setFieldValue('to_date', v)}
                placeholder='Pick date'
                minDate={formik.values.from_date || undefined}
              />
              {!isSameDay && (
                <Select
                  value={formik.values.to_half}
                  onValueChange={v => formik.setFieldValue('to_half', v)}
                >
                  <SelectTrigger className='h-9 rounded-xl text-xs'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HALF_DAY_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {formik.touched.to_date && formik.errors.to_date && (
                <p className='text-[11px] font-semibold text-red-500'>
                  {formik.errors.to_date}
                </p>
              )}
            </div>
          </div>

          {requestedDays !== null && (
            <p className='text-sm font-medium text-foreground bg-primary/5 border border-primary/10 rounded-lg px-3 py-2'>
              You are requesting {requestedDays} day(s) of leave
              {!isUnpaidLeave && requestedDays > availableForType && (
                <span className='text-destructive font-semibold'>
                  {' '}
                  — exceeds your available balance
                </span>
              )}
            </p>
          )}

          {/* Reason */}
          <div className='space-y-2'>
            <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
              Note <span className='text-red-500'>*</span>
            </Label>
            <textarea
              name='reason'
              rows={3}
              placeholder='Brief reason for the leave...'
              value={formik.values.reason}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className='flex w-full rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm transition-all focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none min-h-[80px]'
            />
            {formik.touched.reason && formik.errors.reason && (
              <p className='text-[11px] font-semibold text-red-500'>
                {formik.errors.reason}
              </p>
            )}
          </div>

          {employeeId && (
            <div className='flex items-center gap-2'>
              <input
                type='checkbox'
                id='is_emergency'
                checked={formik.values.is_emergency}
                onChange={e =>
                  formik.setFieldValue('is_emergency', e.target.checked)
                }
                className='h-4 w-4 rounded border-border'
              />
              <Label
                htmlFor='is_emergency'
                className='text-xs font-medium text-muted-foreground'
              >
                Emergency — bypass the notice-period requirement
              </Label>
            </div>
          )}

          {/* Notify — additional people who get an FYI bot DM alongside the
              approver (see alertbot/leaveNotify.js#notifyLeaveRequestCreated). */}
          <div className='space-y-2'>
            <Label className='text-xs font-bold uppercase tracking-wide text-muted-foreground'>
              Notify <span className='text-red-500'>*</span>
            </Label>
            <Popover open={notifyPickerOpen} onOpenChange={setNotifyPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className='w-full justify-between h-11 rounded-xl font-normal'
                >
                  <div className='flex items-center gap-2'>
                    <Users className='w-4 h-4 text-muted-foreground' />
                    <span className='text-sm'>
                      {formik.values.notify.length === 0
                        ? 'Choose people to notify...'
                        : `${formik.values.notify.length} selected`}
                    </span>
                  </div>
                  <ChevronsUpDown className='w-4 h-4 text-muted-foreground opacity-50' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-[var(--radix-popover-trigger-width)] p-1 max-h-[280px] overflow-y-auto rounded-xl'
                align='start'
              >
                {notifyOptions.length === 0 ? (
                  <div className='py-4 text-center text-xs text-muted-foreground'>
                    No employees found
                  </div>
                ) : (
                  notifyOptions.map(emp => {
                    const selected = formik.values.notify.includes(emp.user_id)
                    return (
                      <button
                        key={emp.user_id}
                        type='button'
                        onClick={() =>
                          formik.setFieldValue(
                            'notify',
                            selected
                              ? formik.values.notify.filter(
                                  id => id !== emp.user_id
                                )
                              : [...formik.values.notify, emp.user_id]
                          )
                        }
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md hover:bg-accent transition-colors text-left',
                          selected && 'bg-accent/50 text-primary font-medium'
                        )}
                      >
                        <div
                          className={cn(
                            'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                            selected
                              ? 'bg-primary border-primary'
                              : 'border-muted-foreground/30'
                          )}
                        >
                          {selected && (
                            <Check className='w-2.5 h-2.5 text-primary-foreground stroke-[3]' />
                          )}
                        </div>
                        <span className='truncate'>{emp.user_name}</span>
                      </button>
                    )
                  })
                )}
              </PopoverContent>
            </Popover>
            {formik.values.notify.length > 0 && (
              <div className='flex flex-wrap gap-1.5 pt-1'>
                {formik.values.notify.map(id => {
                  const emp = notifyOptions.find(e => e.user_id === id)
                  return (
                    <span
                      key={id}
                      className='inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-muted text-xs font-medium'
                    >
                      {emp?.user_name ?? `#${id}`}
                      <button
                        type='button'
                        onClick={() =>
                          formik.setFieldValue(
                            'notify',
                            formik.values.notify.filter(v => v !== id)
                          )
                        }
                        className='hover:text-destructive'
                      >
                        <X className='w-3 h-3' />
                      </button>
                    </span>
                  )
                })}
              </div>
            )}
            {formik.touched.notify && formik.errors.notify && (
              <p className='text-[11px] font-semibold text-red-500'>
                {typeof formik.errors.notify === 'string'
                  ? formik.errors.notify
                  : 'Select at least one person to notify'}
              </p>
            )}
          </div>

          {/* Attachment — optional, mirrors the attachment field on ClickUp's
              own leave request form (e.g. a medical certificate). Uploaded to
              the ClickUp task's native Attachments after the request is
              created (routes/leaveRequest.routes.js). */}
          <div className='space-y-2'>
            <Label className='block text-xs font-bold uppercase tracking-wide text-muted-foreground'>
              Attachment
            </Label>
            <input
              type='file'
              ref={attachmentInputRef}
              className='hidden'
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) {
                  if (file.size > MAX_ATTACHMENT_SIZE) {
                    toast.error('File size exceeds 10MB limit')
                  } else {
                    formik.setFieldValue('attachment', file)
                  }
                }
                if (attachmentInputRef.current) {
                  attachmentInputRef.current.value = ''
                }
              }}
            />
            {formik.values.attachment ? (
              <span className='inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1.5 rounded-lg bg-muted text-xs font-medium max-w-full'>
                <Paperclip className='w-3.5 h-3.5 shrink-0 text-muted-foreground' />
                <span className='truncate'>
                  {formik.values.attachment.name}
                </span>
                <button
                  type='button'
                  onClick={() => formik.setFieldValue('attachment', null)}
                  className='hover:text-destructive shrink-0'
                >
                  <X className='w-3 h-3' />
                </button>
              </span>
            ) : (
              <Button
                type='button'
                variant='outline'
                onClick={() => attachmentInputRef.current?.click()}
                className='h-9 rounded-xl text-xs gap-1.5'
              >
                <Paperclip className='w-3.5 h-3.5' />
                Attach File
              </Button>
            )}
          </div>

          <div className='flex justify-end gap-3 pt-4 border-t border-border/50'>
            <Button
              type='button'
              variant='ghost'
              onClick={() => {
                formik.resetForm()
                isClose()
              }}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Submitting...' : 'Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default RequestLeaveDialog
